/**
 * The value sets packet fields take. Each is a table of named codes with a type of the same name for its values, the
 * way an enum reads, but as plain objects, which type stripping and bundlers pass through untouched.
 */

/**
 * Protocol time in units of 10 microseconds, counted from the server's start. A uint32 on the wire, it wraps about
 * every 11.9 hours, and the server may also start it again from 0, so compare two clocks modulo 2^32.
 */
export type ClockTime = number;

/** A team's ID: each player's own ID in FFA and BTR, one of CTF_TEAMS in CTF. */
export type TeamId = number;

/** The value type of a table of codes. */
type ValueOf<T> = T[keyof T];

/** Airplane types. */
export const PLANE_TYPES = {
  PREDATOR: 1,
  GOLIATH: 2,
  MOHAWK: 3,
  TORNADO: 4,
  PROWLER: 5,
} as const;

export type PLANE_TYPES = ValueOf<typeof PLANE_TYPES>;

/** ERROR codes. */
export const SERVER_ERRORS = {
  PACKET_FLOODING_DISCONNECT: 1,
  PACKET_FLOODING_BAN: 2,
  GLOBAL_BAN: 3,
  UNKNOWN_ERROR: 4,
  REQUIRED_INACTIVITY_AND_HEALTH_TO_RESPAWN: 5,
  AFK_DISCONNECT: 6,
  PLAYER_KICKED: 7,
  INVALID_LOGIN_DATA: 8,
  INCORRECT_PROTOCOL: 9,
  ACCOUNT_BANNED: 10,
  ALREADY_LOGGED_IN: 11,
  FORBIDDEN_TO_CHANGE_PLANE_IN_BTR: 12,
  REQUIRED_INACTIVITY_AND_HEALTH_TO_SPECTATE: 13,
  NOT_ENOUGH_UPGRADES: 20,
  CHAT_SPAMMING: 30,
  FLAG_CHANGE_TOO_FAST: 31,
  UNKNOWN_COMMAND: 100,
  SYNC_AUTH_INVALID: 201,
  SYNC_INIT_INVALID: 202,
  SYNC_UPDATE_INVALID: 204,
  SYNC_ACK_INVALID: 205,
  SYNC_NOT_CONFIGURED: 211,
  SYNC_NOT_READY: 212,
} as const;

export type SERVER_ERRORS = ValueOf<typeof SERVER_ERRORS>;

/** SERVER_MESSAGE types. */
export const SERVER_MESSAGE_TYPES = {
  ALERT: 1,
  INFO: 2,
} as const;

export type SERVER_MESSAGE_TYPES = ValueOf<typeof SERVER_MESSAGE_TYPES>;

/** COMMAND_REPLY types. */
export const COMMAND_REPLY_TYPES = {
  /** A notice at the bottom of the chat. */
  CHAT: 0,
  /** A popup, for debugging only. */
  DEBUG: 1,
} as const;

export type COMMAND_REPLY_TYPES = ValueOf<typeof COMMAND_REPLY_TYPES>;

/** EVENT_LEAVEHORIZON types: what left the view. */
export const LEAVE_HORIZON_TYPES = {
  PLAYER: 0,
  MOB: 1,
} as const;

export type LEAVE_HORIZON_TYPES = ValueOf<typeof LEAVE_HORIZON_TYPES>;

/** Mob types: projectiles, boxes, and the BTR firewall, which PLAYER_HIT names as the cause. */
export const MOB_TYPES = {
  PLAYER: 0,
  PREDATOR_MISSILE: 1,
  GOLIATH_MISSILE: 2,
  COPTER_MISSILE: 3,
  UPGRADE: 4,
  TORNADO_MISSILE: 5,
  TORNADO_SMALL_MISSILE: 6,
  PROWLER_MISSILE: 7,
  SHIELD: 8,
  INFERNO: 9,
  FIREWALL: 200,
} as const;

export type MOB_TYPES = ValueOf<typeof MOB_TYPES>;

/**
 * MOB_DESPAWN types: a box or projectile expired, or a player picked up a box. That player gets PICKUP, and everyone
 * else who sees the box EXPIRED.
 */
export const MOB_DESPAWN_TYPES = {
  EXPIRED: 0,
  PICKUP: 1,
} as const;

export type MOB_DESPAWN_TYPES = ValueOf<typeof MOB_DESPAWN_TYPES>;

/** PLAYER_LEVEL types: the level told on login, or a level up. */
export const PLAYER_LEVEL_UPDATE_TYPES = {
  INFORM: 0,
  LEVELUP: 1,
} as const;

export type PLAYER_LEVEL_UPDATE_TYPES = ValueOf<typeof PLAYER_LEVEL_UPDATE_TYPES>;

/** A player's status. */
export const PLAYER_STATUS = {
  /** On the map. */
  ALIVE: 0,
  /** Dead or spectating. */
  INACTIVE: 1,
} as const;

export type PLAYER_STATUS = ValueOf<typeof PLAYER_STATUS>;

/**
 * PLAYER_UPGRADE types: the kind just applied, or LOST when the counts changed without one, as on a death, a reconnect,
 * or a server command that drops or resets them.
 */
export const PLAYER_UPGRADE_TYPES = {
  LOST: 0,
  SPEED: 1,
  DEFENSE: 2,
  ENERGY: 3,
  MISSILE: 4,
} as const;

export type PLAYER_UPGRADE_TYPES = ValueOf<typeof PLAYER_UPGRADE_TYPES>;

/** SERVER_CUSTOM types. */
export const SERVER_CUSTOM_TYPES = {
  /**
   * The end of a BTR match. With CTF, the only types Q-bots accept, so no other type goes to them.
   */
  BTR: 1,
  /** The end of a CTF match. */
  CTF: 2,
  /** Suggests the player switch to another game server. */
  SWITCH_GAME_SUGGESTION: 100,
} as const;

export type SERVER_CUSTOM_TYPES = ValueOf<typeof SERVER_CUSTOM_TYPES>;

/** PLAYER_POWERUP types. */
export const PLAYER_POWERUP_TYPES = {
  SHIELD: 1,
  INFERNO: 2,
} as const;

export type PLAYER_POWERUP_TYPES = ValueOf<typeof PLAYER_POWERUP_TYPES>;

/** CTF teams, which are also the flags' teams. */
export const CTF_TEAMS = {
  BLUE: 1,
  RED: 2,
} as const;

export type CTF_TEAMS = ValueOf<typeof CTF_TEAMS>;

/** GAME_FLAG types. */
export const CTF_FLAG_STATE = {
  /** Resting at its base or dropped. */
  STATIC: 1,
  /** Carried by a player. */
  DYNAMIC: 2,
} as const;

export type CTF_FLAG_STATE = ValueOf<typeof CTF_FLAG_STATE>;

/** GAME_FIREWALL statuses. */
export const BTR_FIREWALL_STATUS = {
  INACTIVE: 0,
  ACTIVE: 1,
} as const;

export type BTR_FIREWALL_STATUS = ValueOf<typeof BTR_FIREWALL_STATUS>;

/** Game types, as LOGIN gives the room's. */
export const GAME_TYPES = {
  FFA: 1,
  CTF: 2,
  BTR: 3,
} as const;

export type GAME_TYPES = ValueOf<typeof GAME_TYPES>;

/** The control keys a KEY packet presses or releases. */
export const KEY_CODES = {
  UP: 1,
  DOWN: 2,
  LEFT: 3,
  RIGHT: 4,
  FIRE: 5,
  SPECIAL: 6,
} as const;

export type KEY_CODES = ValueOf<typeof KEY_CODES>;

/** The control keys' names by code. */
export const KEY_NAMES = {
  1: 'UP',
  2: 'DOWN',
  3: 'LEFT',
  4: 'RIGHT',
  5: 'FIRE',
  6: 'SPECIAL',
} as const satisfies Record<KEY_CODES, keyof typeof KEY_CODES>;
